import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, Trash2, AlertTriangle, Check, HelpCircle, X } from 'lucide-react';
import BlendToolbar from '../components/toolbars/BlendToolbar';

const BlendForm = () => {
  const [blendName, setBlendName] = useState('');
  const [blendDate, setBlendDate] = useState('');
  const [blendNotes, setBlendNotes] = useState('');
  const [targetBlendSize, setTargetBlendSize] = useState(0);
  const [components, setComponents] = useState([]);
  const [availableWines, setAvailableWines] = useState([]);
  const [wineContainers, setWineContainers] = useState({});
  const [showBarrelModal, setShowBarrelModal] = useState(null);
  const [targetVarietal, setTargetVarietal] = useState('');
  const [targetAppellation, setTargetAppellation] = useState('');
  const [targetVintage, setTargetVintage] = useState('');
  const [vintageOptions, setVintageOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { blendId } = useParams();
  const navigate = useNavigate();
  const isEditing = !!blendId;
  const [varietals, setVarietals] = useState([]);
  const [showVarietalTooltip, setShowVarietalTooltip] = useState(false);
  const [showAppellationTooltip, setShowAppellationTooltip] = useState(false);
  const [showVintageTooltip, setShowVintageTooltip] = useState(false);

  // Helper functions
  const calculateAverageAlcohol = () => {
    const validComponents = components.filter(c => c.alcohol);
    if (validComponents.length === 0) return null;

    const avgAlcohol = validComponents.reduce(
      (sum, component) => sum + parseFloat(component.alcohol || 0),
      0
    ) / validComponents.length;

    return avgAlcohol.toFixed(2);
  };

  const fetchContainersForWine = useCallback(async (wineID) => {
    try {
      const response = await axios.get(`/api/wines/${wineID}/containers`);
      setWineContainers(prev => ({
        ...prev,
        [wineID]: response.data
      }));
    } catch (err) {
      console.error('Error fetching containers:', err);
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      // Fetch varietals list
      const varietalsResponse = await axios.get('/api/varietals');
      setVarietals(varietalsResponse.data || []);

      const winesResponse = await axios.get('/api/wines', {
        params: { active: 1, limit: 100 },
      });

      const enhancedWines = winesResponse.data.wines.map(wine => ({
        ...wine,
        displayName: `${wine.varietal} ${wine.vintage} (${wine.vineyard || 'Unknown'})`,
        finalAlcohol: wine.finalAlc || '',
      }));

      setAvailableWines(enhancedWines);

      if (isEditing) {
        const blendResponse = await axios.get(`/api/blends/${blendId}`);
        const blend = blendResponse.data;

        setBlendName(blend.blendName);
        setBlendDate(
          blend.blendDate
            ? new Date(blend.blendDate).toISOString().split('T')[0]
            : new Date().toISOString().split('T')[0]
        );
        setBlendNotes(blend.blendNotes || '');
        setTargetBlendSize(parseFloat(blend.totalVolume) || 0);

        setTargetVarietal(blend.targetVarietal || '');
        setTargetAppellation(blend.targetAppellation || '');
        setTargetVintage(blend.targetVintage || '');

        const preparedComponents = blend.components.map(component => {
          const matchedWine = enhancedWines.find(wine => wine.wineID === component.wineID);
          return {
            wineID: component.wineID,
            volume: parseFloat(component.volume),
            percentage: parseFloat(component.percentage),
            alcohol: matchedWine?.finalAlcohol || component.alcohol || '',
            varietalDetail: component.varietal,
            vintage: matchedWine?.vintage || '',
            varietal: matchedWine?.varietal || '',
            appellation: matchedWine?.appellation || '',
            selectedBarrels: component.barrels?.map(b => b.containerID) || [],
          };
        });

        setComponents(preparedComponents);
      } else {
        setComponents([{ 
          wineID: '', 
          volume: 0, 
          percentage: 0, 
          alcohol: '', 
          varietalDetail: '',
          vintage: '',
          varietal: '',
          appellation: '',
          selectedBarrels: []
        }]);
      }

      setLoading(false);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load blend details');
      setLoading(false);
    }
  }, [blendId, isEditing]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const years = ['Non-Vintage'];
    for (let i = currentYear; i >= currentYear - 12; i--) {
      years.push(i.toString());
    }
    setVintageOptions(years);
  }, []);

  const addComponent = () => {
    setComponents(prev => [...prev, { 
      wineID: '', 
      volume: 0, 
      percentage: 0, 
      alcohol: '', 
      varietalDetail: '',
      vintage: '',
      varietal: '',
      appellation: '',
      selectedBarrels: []
    }]);
  };

  const updateComponent = (index, field, value) => {
    const updatedComponents = [...components];
    if (field === 'wineID') {
      const selectedWine = availableWines.find(wine => wine.wineID === parseInt(value));
      updatedComponents[index] = {
        ...updatedComponents[index],
        wineID: value,
        alcohol: selectedWine?.finalAlcohol || '',
        vintage: selectedWine?.vintage || '',
        varietal: selectedWine?.varietal || '',
        appellation: selectedWine?.appellation || '',
        selectedBarrels: [] // Add this to track selected barrels
      };
      
      // Fetch containers for this wine
      if (value) {
        fetchContainersForWine(value);
      }
    } else {
      updatedComponents[index][field] = value;
    }
    setComponents(updatedComponents);
  };

  const updateVolume = (index, volume) => {
    const updatedComponents = [...components];
    updatedComponents[index].volume = parseFloat(volume) || 0;

    const totalVolume = updatedComponents.reduce((sum, comp) => sum + parseFloat(comp.volume || 0), 0);
    updatedComponents.forEach(component => {
      component.percentage = totalVolume > 0 ? ((component.volume / totalVolume) * 100).toFixed(2) : 0;
    });

    setComponents(updatedComponents);
  };

  const removeComponent = index => {
    setComponents(components.filter((_, i) => i !== index));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    try {
      const blendData = {
        blendName,
        blendDate,
        blendNotes,
        totalVolume: targetBlendSize,
        targetVarietal,
        targetAppellation,
        targetVintage,
        components: components.map(c => ({
          wineID: c.wineID,
          volume: parseFloat(c.volume),
          percentage: parseFloat(c.percentage),
          barrels: c.selectedBarrels || [] // Include selected barrels
        })),
      };
  
      if (isEditing) {
        await axios.put(`/api/blends/${blendId}`, blendData);
      } else {
        await axios.post('/api/blends', blendData);
      }
      navigate('/blends');
    } catch (err) {
      console.error('Error saving blend:', err);
      setError('Failed to save blend');
    }
  };

  // Toggle barrel selection
  const toggleBarrelSelection = (componentIndex, containerId) => {
    const updatedComponents = [...components];
    const component = updatedComponents[componentIndex];
    
    // Initialize selectedBarrels if it doesn't exist
    if (!component.selectedBarrels) {
      component.selectedBarrels = [];
    }
    
    // Toggle the barrel selection
    if (component.selectedBarrels.includes(containerId)) {
      component.selectedBarrels = component.selectedBarrels.filter(id => id !== containerId);
    } else {
      component.selectedBarrels.push(containerId);
    }
    
    setComponents(updatedComponents);
  };

  const finalizeBlend = async () => {
    if (!blendName || components.length === 0) {
      setError('Please complete the blend details before finalizing');
      return;
    }
  
    // Confirm with user
    if (!window.confirm('Are you sure you want to finalize this blend? This will create a new wine lot and move barrels.')) {
      return;
    }
  
    try {
      const response = await axios.post(`/api/blends/${blendId}/finalize`, {
        targetVarietal,
        targetAppellation,
        targetVintage
      });
  
      alert(`Blend finalized successfully! New wine ID: ${response.data.newWineID}`);
      navigate(`/wines/${response.data.newWineID}`); // Navigate to the new wine
    } catch (err) {
      console.error('Error finalizing blend:', err);
      setError('Failed to finalize blend: ' + (err.response?.data?.message || err.message));
    }
  };

  // Calculate blend statistics for TTB compliance and feedback
  const blendStats = useMemo(() => {
    const totalGallons = components.reduce((sum, comp) => sum + parseFloat(comp.volume || 0), 0);
    
    // Group by varietals
    const varietalGroups = {};
    components.forEach(comp => {
      if (!comp.varietal || !comp.volume) return;
      
      const varietal = comp.varietal;
      if (!varietalGroups[varietal]) {
        varietalGroups[varietal] = 0;
      }
      varietalGroups[varietal] += parseFloat(comp.volume);
    });

    // Group by vintages
    const vintageGroups = {};
    components.forEach(comp => {
      if (!comp.vintage || !comp.volume) return;
      
      const vintage = comp.vintage;
      if (!vintageGroups[vintage]) {
        vintageGroups[vintage] = 0;
      }
      vintageGroups[vintage] += parseFloat(comp.volume);
    });

    // Group by appellations
    const appellationGroups = {};
    components.forEach(comp => {
      if (!comp.appellation || !comp.volume) return;
      
      const appellation = comp.appellation;
      if (!appellationGroups[appellation]) {
        appellationGroups[appellation] = 0;
      }
      appellationGroups[appellation] += parseFloat(comp.volume);
    });

    // Calculate percentages and find dominants
    let dominantVarietal = { name: '', percentage: 0 };
    let dominantVintage = { year: '', percentage: 0 };
    let dominantAppellation = { name: '', percentage: 0 };

    Object.entries(varietalGroups).forEach(([varietal, volume]) => {
      const percentage = (volume / totalGallons) * 100;
      if (percentage > dominantVarietal.percentage) {
        dominantVarietal = { name: varietal, percentage };
      }
    });

    Object.entries(vintageGroups).forEach(([vintage, volume]) => {
      const percentage = (volume / totalGallons) * 100;
      if (percentage > dominantVintage.percentage) {
        dominantVintage = { year: vintage, percentage };
      }
    });

    Object.entries(appellationGroups).forEach(([appellation, volume]) => {
      const percentage = (volume / totalGallons) * 100;
      if (percentage > dominantAppellation.percentage) {
        dominantAppellation = { name: appellation, percentage };
      }
    });

    // Calculate if designations are valid according to TTB rules
    const isValidVarietal = dominantVarietal.percentage >= 75;
    const isValidVintage = dominantVintage.percentage >= 95;
    const isValidAppellation = dominantAppellation.percentage >= 85;

    // Calculate progress toward target volume
    const volumeProgress = targetBlendSize > 0 ? (totalGallons / targetBlendSize) * 100 : 0;

    return {
      totalGallons,
      dominantVarietal,
      dominantVintage,
      dominantAppellation,
      isValidVarietal,
      isValidVintage,
      isValidAppellation,
      volumeProgress: Math.min(volumeProgress, 100), // Cap at 100%
      volumeDifference: targetBlendSize - totalGallons
    };
  }, [components, targetBlendSize]);

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="d-flex justify-content-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  const renderBarrelSelectionModal = () => {
    if (showBarrelModal === null) return null;
    
    const component = components[showBarrelModal];
    const containers = wineContainers[component.wineID] || [];
    
    return (
      <div className="modal show d-block" tabIndex="-1">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Select Barrels</h5>
              <button type="button" className="btn-close" onClick={() => setShowBarrelModal(null)}></button>
            </div>
            <div className="modal-body">
              {containers.length === 0 ? (
                <p>No barrels found for this wine.</p>
              ) : (
                <div className="row">
                  {containers.map(container => (
                    <div key={container.containerID} className="col-md-4 mb-2">
                      <div className={`card ${component.selectedBarrels?.includes(container.containerID) ? 'bg-primary text-white' : ''}`}>
                        <div className="card-body">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`barrel-${container.containerID}`}
                              checked={component.selectedBarrels?.includes(container.containerID) || false}
                              onChange={() => toggleBarrelSelection(showBarrelModal, container.containerID)}
                            />
                            <label className="form-check-label" htmlFor={`barrel-${container.containerID}`}>
                              <strong>ID: {container.containerID}</strong> - {container.cntDesc} ({container.cntGallons} gal)
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowBarrelModal(null)}>Close</button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderTooltipContent = (type) => {
    switch(type) {
      case 'varietal':
        return (
          <div className="ttb-tooltip-content">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="mb-0">Varietal Designation Rules</h6>
              <button className="btn btn-sm" onClick={() => setShowVarietalTooltip(false)}>
                <X size={18} />
              </button>
            </div>
            <p>TTB requires at least <strong>75%</strong> of the wine to be made from the named grape variety to carry that varietal designation.</p>
            <p>For example, a wine labeled as "Cabernet Sauvignon" must contain at least 75% Cabernet Sauvignon grapes.</p>
            {blendStats.dominantVarietal.name && (
              <div className="alert alert-info">
                <small>
                  Current dominant varietal: <strong>{blendStats.dominantVarietal.name}</strong> at {blendStats.dominantVarietal.percentage.toFixed(1)}% 
                  {!blendStats.isValidVarietal && (
                    <span> (needs {(75 - blendStats.dominantVarietal.percentage).toFixed(1)}% more)</span>
                  )}
                </small>
              </div>
            )}
          </div>
        );
      case 'appellation':
        return (
          <div className="ttb-tooltip-content">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="mb-0">Appellation Designation Rules</h6>
              <button className="btn btn-sm" onClick={() => setShowAppellationTooltip(false)}>
                <X size={18} />
              </button>
            </div>
            <p>To claim an American Viticultural Area (AVA) or appellation, <strong>85%</strong> of grapes must come from that area.</p>
            <p>For county or state appellations, at least 75% of the grapes must be grown in that area.</p>
            {blendStats.dominantAppellation.name && (
              <div className="alert alert-info">
                <small>
                  Current dominant appellation: <strong>{blendStats.dominantAppellation.name}</strong> at {blendStats.dominantAppellation.percentage.toFixed(1)}% 
                  {!blendStats.isValidAppellation && (
                    <span> (needs {(85 - blendStats.dominantAppellation.percentage).toFixed(1)}% more)</span>
                  )}
                </small>
              </div>
            )}
          </div>
        );
      case 'vintage':
        return (
          <div className="ttb-tooltip-content">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="mb-0">Vintage Designation Rules</h6>
              <button className="btn btn-sm" onClick={() => setShowVintageTooltip(false)}>
                <X size={18} />
              </button>
            </div>
            <p>For vintage dated wines, at least <strong>95%</strong> of the wine must be from grapes harvested in the stated year.</p>
            <p>The vintage date refers to the year the grapes were harvested, not when the wine was bottled.</p>
            {blendStats.dominantVintage.year && (
              <div className="alert alert-info">
                <small>
                  Current dominant vintage: <strong>{blendStats.dominantVintage.year}</strong> at {blendStats.dominantVintage.percentage.toFixed(1)}% 
                  {!blendStats.isValidVintage && (
                    <span> (needs {(95 - blendStats.dominantVintage.percentage).toFixed(1)}% more)</span>
                  )}
                </small>
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <BlendToolbar 
        onSave={handleSubmit}
        onFinalize={finalizeBlend}
        averageAlcohol={calculateAverageAlcohol()}
        isEditing={isEditing}
      />
      
      <div className="container mt-4">
        {error && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            {error}
            <button type="button" className="btn-close" onClick={() => setError(null)}></button>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Combined Info Card with Blend Summary */}
          <div className="card mb-4">
            <div className="card-body">
              <div className="row">
                <div className="col-md-4 mb-3">
                  <label className="form-label">Blend Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={blendName}
                    onChange={e => setBlendName(e.target.value)}
                    required
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label">Blend Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={blendDate}
                    onChange={e => setBlendDate(e.target.value)}
                    required
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label">Target Size (Gallons)</label>
                  <div className="input-group">
                    <input
                      type="number"
                      className="form-control"
                      value={targetBlendSize}
                      onChange={e => setTargetBlendSize(parseFloat(e.target.value))}
                      min="0"
                      step="0.01"
                    />
                    <span className="input-group-text">
                      {(targetBlendSize / 2.377).toFixed(2)} cases
                    </span>
                  </div>
                </div>
              </div>
              <div className="row mt-2">
                        <div className="col-md-4 mb-3">
                          <label className="form-label">Target Varietal</label>
                          <select
                            className="form-select"
                            value={targetVarietal}
                            onChange={e => setTargetVarietal(e.target.value)}
                          >
                            <option value="">Select Varietal</option>
                            {varietals.map((varietal, index) => (
                              <option key={index} value={varietal}>
                                {varietal}
                              </option>
                            ))}
                          </select>
                          {targetVarietal && !blendStats.isValidVarietal && (
                            <small className="text-info">
                              Note: Current blend components don't meet 75% minimum for {targetVarietal}
                            </small>
                          )}
                        </div>
                        <div className="col-md-4 mb-3">
                          <label className="form-label">Target Appellation</label>
                          <select
                            className="form-control"
                            value={targetAppellation}
                            onChange={e => setTargetAppellation(e.target.value)}
                          >
                            <option value="">Select Appellation</option>
                            <option value="Lodi">Lodi</option>
                            <option value="Amador">Amador</option>
                            <option value="California">California</option>
                          </select>
                          {targetAppellation && !blendStats.isValidAppellation && (
                            <small className="text-info">
                              Note: Current blend components don't meet 85% minimum for {targetAppellation}
                            </small>
                          )}
                        </div>
                        <div className="col-md-4 mb-3">
                          <label className="form-label">Target Vintage</label>
                          <select
                            className="form-control"
                            value={targetVintage}
                            onChange={e => setTargetVintage(e.target.value)}
                          >
                            <option value="">Select Vintage</option>
                            {vintageOptions.map((vintage, index) => (
                              <option key={index} value={vintage === 'Non-Vintage' ? '' : vintage}>
                                {vintage}
                              </option>
                            ))}
                          </select>
                          {targetVintage && !blendStats.isValidVintage && (
                            <small className="text-info">
                              Note: Current blend components don't meet 95% minimum for {targetVintage}
                            </small>
                          )}
                        </div>
                      </div>
              <div className="mb-3">
                <label className="form-label">Notes</label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={blendNotes}
                  onChange={e => setBlendNotes(e.target.value)}
                  placeholder="Enter any notes about this blend..."
                />
              </div>

              {/* Blend Summary Section */}
              <div className="mt-4 mb-2">
                <h5 className="border-bottom pb-2">Blend Summary</h5>
                <div className="row mt-3">
                  <div className="col-md-4 mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <label className="form-label mb-0">Current Volume: <strong>{blendStats.totalGallons.toFixed(2)} gal</strong></label>
                      <small>({(blendStats.totalGallons / 2.377).toFixed(2)} cases)</small>
                    </div>
                    <div className="progress" style={{height: "20px"}}>
                      <div 
                        className={`progress-bar ${blendStats.volumeProgress < 90 ? 'bg-warning' : 'bg-success'}`} 
                        role="progressbar" 
                        style={{width: `${blendStats.volumeProgress}%`}}
                        aria-valuenow={blendStats.volumeProgress} 
                        aria-valuemin="0" 
                        aria-valuemax="100">
                        {blendStats.volumeProgress.toFixed(0)}%
                      </div>
                    </div>
                    <small className="text-muted">
                      {blendStats.volumeDifference > 0 
                        ? `${blendStats.volumeDifference.toFixed(2)} gallons needed to reach target`
                        : blendStats.volumeDifference < 0
                          ? `${Math.abs(blendStats.volumeDifference).toFixed(2)} gallons over target`
                          : 'Target volume reached'}
                    </small>
                  </div>
                  <div className="col-md-8">
                    <div className="row">
                      <div className="col-md-4">
                        <div className="alert alert-light mb-2 position-relative">
                          <div className="d-flex align-items-center">
                            {blendStats.isValidVarietal ? 
                              <Check size={16} className="text-success me-2" /> : 
                              <AlertTriangle size={16} className="text-info me-2" />
                            }
                            <span className="fs-6 fw-bold">Varietal Status</span>
                            <div className="ms-auto">
                              <HelpCircle 
                                size={16} 
                                className="text-primary cursor-pointer" 
                                onClick={() => setShowVarietalTooltip(!showVarietalTooltip)}
                              />
                            </div>
                          </div>
                          <div className="mt-1">
                            <small>
                              {blendStats.dominantVarietal.name ? 
                                `${blendStats.dominantVarietal.name}: ${blendStats.dominantVarietal.percentage.toFixed(1)}%` : 
                                'No dominant varietal'}
                            </small>
                          </div>
                          {!blendStats.isValidVarietal && blendStats.dominantVarietal.name && (
                            <div className="mt-1">
                              <small className="text-info">
                                {`Needs ${(75 - blendStats.dominantVarietal.percentage).toFixed(1)}% more to claim varietal`}
                              </small>
                            </div>
                          )}
                          {showVarietalTooltip && (
                            <div className="ttb-tooltip">
                              {renderTooltipContent('varietal')}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="col-md-4">
                        <div className="alert alert-light mb-2 position-relative">
                          <div className="d-flex align-items-center">
                            {blendStats.isValidVintage ? 
                              <Check size={16} className="text-success me-2" /> : 
                              <AlertTriangle size={16} className="text-info me-2" />
                            }
                            <span className="fs-6 fw-bold">Vintage Status</span>
                            <div className="ms-auto">
                              <HelpCircle 
                                size={16} 
                                className="text-primary cursor-pointer" 
                                onClick={() => setShowVintageTooltip(!showVintageTooltip)} 
                              />
                            </div>
                          </div>
                          <div className="mt-1">
                            <small>
                              {blendStats.dominantVintage.year ? 
                                `${blendStats.dominantVintage.year}: ${blendStats.dominantVintage.percentage.toFixed(1)}%` : 
                                'No dominant vintage'}
                            </small>
                          </div>
                          {!blendStats.isValidVintage && blendStats.dominantVintage.year && (
                            <div className="mt-1">
                              <small className="text-warning">
                                {`Needs ${(95 - blendStats.dominantVintage.percentage).toFixed(1)}% more to claim vintage`}
                              </small>
                            </div>
                          )}
                          {showVintageTooltip && (
                            <div className="ttb-tooltip">
                              {renderTooltipContent('vintage')}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="col-md-4">
                        <div className="alert alert-light mb-2 position-relative">
                          <div className="d-flex align-items-center">
                            {blendStats.isValidAppellation ? 
                              <Check size={16} className="text-success me-2" /> : 
                              <AlertTriangle size={16} className="text-warning me-2" />
                            }
                            <span className="fs-6 fw-bold">Appellation Status</span>
                            <div className="ms-auto">
                              <HelpCircle 
                                size={16} 
                                className="text-primary cursor-pointer" 
                                onClick={() => setShowAppellationTooltip(!showAppellationTooltip)} 
                              />
                            </div>
                          </div>
                          <div className="mt-1">
                            <small>
                              {blendStats.dominantAppellation.name ? 
                                `${blendStats.dominantAppellation.name}: ${blendStats.dominantAppellation.percentage.toFixed(1)}%` : 
                                'No dominant appellation'}
                            </small>
                          </div>
                          {!blendStats.isValidAppellation && blendStats.dominantAppellation.name && (
                            <div className="mt-1">
                              <small className="text-info">
                                {`Needs ${(85 - blendStats.dominantAppellation.percentage).toFixed(1)}% more to claim appellation`}
                              </small>
                            </div>
                          )}
                          {showAppellationTooltip && (
                            <div className="ttb-tooltip">
                              {renderTooltipContent('appellation')}
                            </div>
                          )}
                        </div>
                      </div>
                      
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Components Card */}
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Blend Components</h5>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={addComponent}
              >
                <Plus size={18} className="me-1" />
                Add Component
              </button>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>Wine</th>
                      <th>Volume (Gal)</th>
                      <th>Percentage</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                  {components.map((component, index) => (
                      <tr key={index} className="table-row-hover">
                        <td>
                          <select
                            className="form-select"
                            value={component.wineID}
                            onChange={e => updateComponent(index, 'wineID', e.target.value)}
                          >
                            <option value="">Select Wine</option>
                            {availableWines.map(wine => (
                              <option key={wine.wineID} value={wine.wineID}>
                                {wine.displayName}
                              </option>
                            ))}
                          </select>
                          
                          {/* Add barrel selection if a wine is selected */}
                          {component.wineID && wineContainers[component.wineID] && (
                            <div className="mt-2 barrel-selection">
                              <button 
                                type="button" 
                                className="btn btn-sm btn-outline-secondary" 
                                onClick={() => setShowBarrelModal(index)}
                              >
                                Select Barrels ({component.selectedBarrels?.length || 0} selected)
                              </button>
                              {component.selectedBarrels?.length > 0 && (
                                <div className="mt-1 small text-muted">
                                  Barrel IDs: {component.selectedBarrels.sort((a, b) => a - b).join(', ')}
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                        <td>
                          <input
                            type="number"
                            className="form-control"
                            value={component.volume}
                            onChange={e => updateVolume(index, e.target.value)}
                            min="0"
                            step="0.01"
                          />
                        </td>
                        <td>
                          <div className="input-group">
                            <input
                              type="number"
                              className="form-control"
                              value={component.percentage}
                              readOnly
                            />
                            <span className="input-group-text">%</span>
                          </div>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => removeComponent(index)}
                            disabled={components.length === 1}
                          >
                            <Trash2 size={18} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </form>
      </div>

      {/* Render the barrel selection modal */}
      {renderBarrelSelectionModal()}

      <style>{`
        .table-row-hover:hover td {
          background-color: rgba(0,0,0,.075);
        }
        .table-row-hover {
          transition: background-color 0.2s ease;
        }
        .btn svg {
          vertical-align: middle;
          margin-top: -2px;
        }
        .btn:hover svg {
          transform: scale(1.1);
          transition: transform 0.2s ease;
        }
        .btn:active svg {
          transform: scale(0.95);
        }
        .progress {
          height: 20px !important;
          background-color: #f5f5f5;
          box-shadow: inset 0 1px 2px rgba(0,0,0,.1);
        }
        .alert-light {
          background-color: #f8f9fa;
          border-color: #dae0e5;
        }
        .cursor-pointer {
          cursor: pointer;
        }
        .ttb-tooltip {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          z-index: 100;
          background-color: #fff;
          border: 1px solid #dee2e6;
          border-radius: 0.25rem;
          padding: 1rem;
          box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
          min-width: 300px;
        }
        .ttb-tooltip-content {
          font-size: 0.875rem;
        }
        .ttb-tooltip-content p {
          margin-bottom: 0.5rem;
        }
      `}</style>
    </>
  );
};

export default BlendForm;